import { useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";

const errText = (e, fallback) => {
  const det = e.response?.data?.detail;
  if (typeof det === "string") return det;
  if (Array.isArray(det)) return det.map((d) => d.msg).join(" ");
  return det?.message || fallback;
};

export function SetPasswordModal({ target, onClose, onDone }) {
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) return setError("Password must be at least 8 characters");
    if (pw !== confirm) return setError("Passwords do not match");
    setBusy(true);
    try {
      await api.post(`/admin/users/${target.id}/set-password`, { password: pw });
      toast.success(`Password set for ${target.email} · signed out everywhere`);
      onDone?.();
      onClose();
    } catch (e2) {
      setError(errText(e2, "Could not set password"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="set-password-modal">
      <form className="modal-card" style={{ maxWidth: 460, padding: 28 }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="eyebrow" style={{ color: "var(--red-light)" }}>— Admin Action</div>
        <h2 className="serif" style={{ fontSize: 28, lineHeight: 1.05, margin: "8px 0 10px", overflowWrap: "anywhere" }}>
          Set password for {target.email}
        </h2>
        <p style={{ color: "var(--text-mute)", fontSize: 13.5, marginBottom: 16 }}>
          They'll be signed out of every device. Share the new password with them yourself.
        </p>
        <input type="password" className="lq-input" placeholder="New password (min 8)" value={pw} onChange={(e) => setPw(e.target.value)} autoFocus data-testid="set-password-input" style={{ marginBottom: 10 }} />
        <input type="password" className="lq-input" placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="set-password-confirm-input" />
        {error && <div className="data-line" style={{ color: "var(--red-light)", marginTop: 10 }} data-testid="set-password-error">{error}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
          <button type="button" className="btn btn-ghost" onClick={onClose} data-testid="set-password-cancel-btn">Cancel</button>
          <button type="submit" className="btn btn-danger" disabled={busy} data-testid="set-password-submit-btn">{busy ? "Saving…" : "Set Password"}</button>
        </div>
      </form>
    </div>
  );
}

export function ChangeOwnPassword({ email }) {
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!cur) return setError("Current password required");
    if (pw.length < 8) return setError("New password must be at least 8 characters");
    if (pw !== confirm) return setError("New password and confirmation do not match");
    setBusy(true);
    try {
      await api.post("/profile/change-password", { current_password: cur, new_password: pw });
      setCur(""); setPw(""); setConfirm("");
      toast.success("Your password was updated");
    } catch (e2) {
      setError(e2.response?.data?.detail?.error === "current_password_incorrect" ? "Current password is incorrect" : errText(e2, "Password update failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="settings-section" style={{ maxWidth: 520 }} onSubmit={submit} data-testid="admin-change-password-form">
      <div className="settings-section-head">
        <div className="settings-section-icon">⊕</div>
        <div className="settings-section-title">Change my password · {email}</div>
      </div>
      <div className="settings-section-body" style={{ display: "grid", gap: 10 }}>
        <input type="password" className="lq-input" placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="admin-current-password" />
        <input type="password" className="lq-input" placeholder="New password (min 8)" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="admin-new-password" />
        <input type="password" className="lq-input" placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="admin-confirm-password" />
        {error && <div className="data-line" style={{ color: "var(--red-light)" }} data-testid="admin-change-password-error">{error}</div>}
        <div>
          <button type="submit" className="btn btn-primary" disabled={busy} data-testid="admin-change-password-submit">{busy ? "Updating…" : "Update Password"}</button>
        </div>
      </div>
    </form>
  );
}
